// ═══════════════════════════════════════════════════════════════════════════
//  SHARED MARKET FEED — ONE /ws/live-feed WebSocket for the WHOLE APP.
//
//  Promoted from components/SuperChart/sharedFeed.ts (2026-07-10 audit: only
//  SuperChart panes shared a socket; OrderLine ran its own 2s REST /quote
//  poll and other modules each opened private transports — the same NIFTY
//  tick travelled 4 different routes and REST pollers fed the Dhan 429
//  stampede). sharedFeed.ts now re-exports from here, so SuperChart and any
//  other module multiplex the SAME socket.
//
//  API:
//    subscribeFeed(onMsg, onStatus?)       — raw frame stream (SuperChart panes)
//    subscribeLTP(symbol, cb)              — per-symbol LTP ticks + cache-warm start
//    getLastLTP(symbol)                    — sync cache peek {ltp, at} | null
//
//  Lifecycle: ref-counted — socket opens with the first subscriber, closes
//  with the last; capped-backoff reconnect + half-open stale watchdog.
// ═══════════════════════════════════════════════════════════════════════════

type FeedListener = (msg: any) => void;
type StatusListener = (s: 'open' | 'closed') => void;
type LtpListener = (ltp: number, at: number) => void;

let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let backoffMs = 2000;
const MAX_BACKOFF_MS = 30000;

// Stale-watchdog: the server streams ~45 msgs/sec, so ANY gap > STALE_MS means
// the socket went HALF-OPEN (TCP died but no close event fired — laptop sleep,
// proxy/LB idle-kill, network blip). We poll for that and force a reconnect.
let lastMsgAt = 0;
let staleTimer: ReturnType<typeof setInterval> | null = null;
const STALE_MS = 10000;

const listeners = new Set<FeedListener>();
const statusListeners = new Set<StatusListener>();

// ── Per-symbol LTP layer ────────────────────────────────────────────────────
// Broadcaster keys vary ("NIFTY_50_23150_CE" vs "NIFTY 23150 CE") — normalize
// both sides the same way ChartPane does.
const normSym = (s: string) =>
  String(s || '').toUpperCase().replace(/[\s_]/g, '').replace('NIFTY50', 'NIFTY');

const ltpCache = new Map<string, { ltp: number; at: number }>();
const ltpListeners = new Map<string, Set<LtpListener>>();

function dispatchLtp(data: any) {
  const key = normSym(data?.symbol);
  if (!key) return;
  const ltp = Number(data.close ?? data.ltp);
  if (!ltp || ltp <= 0) return;
  const at = Date.now();
  // KALI Phase 3 P3-H6: bounded (LRU-ish) - a re-insert moves the key to the end; past 5,000 symbols the oldest go.
  ltpCache.delete(key);
  ltpCache.set(key, { ltp, at });
  if (ltpCache.size > 5000) {
    const it = ltpCache.keys();
    for (let i = 0; i < 500; i++) { const k = it.next(); if (k.done) break; ltpCache.delete(k.value); }
  }
  const subs = ltpListeners.get(key);
  if (subs) subs.forEach(f => { try { f(ltp, at); } catch {} });
}

function totalSubs(): number {
  let n = listeners.size;
  ltpListeners.forEach(s => { n += s.size; });
  return n;
}

// ── PAGE LIFECYCLE (KALI mobile audit M-H4 (h), 2026-10-04) ────────────────
// iOS counts a background tab's CPU/memory before it suspends or kills it, and a
// socket left open in the background is both. On a PHONE: hidden for 30 s ->
// the socket is closed (subscribers are kept); pagehide -> closed at once.
// Everywhere: visible / pageshow / online -> reconnect IMMEDIATELY instead of
// waiting out a backoff of up to 30 s with a dead socket.
const HIDDEN_SUSPEND_MS = 30000;
let suspended = false;
let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
const isPhoneLike = (): boolean => {
  try { return window.matchMedia('(max-width: 767px)').matches; } catch { return false; }
};

function suspendFeed() {
  suspended = true;
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  if (ws) {
    try { ws.onclose = null; ws.close(); } catch {}
    ws = null;
    statusListeners.forEach(f => { try { f('closed'); } catch {} });
  }
}

function resumeFeed() {
  if (hiddenTimer) { clearTimeout(hiddenTimer); hiddenTimer = null; }
  suspended = false;
  if (!totalSubs()) return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  backoffMs = 2000;
  ensureSocket();
  startWatchdog();
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (!isPhoneLike()) return;
      if (hiddenTimer) clearTimeout(hiddenTimer);
      hiddenTimer = setTimeout(() => { hiddenTimer = null; if (document.hidden) suspendFeed(); }, HIDDEN_SUSPEND_MS);
    } else {
      resumeFeed();
    }
  });
  window.addEventListener('pagehide', () => { if (isPhoneLike()) suspendFeed(); });
  window.addEventListener('pageshow', () => { if (!document.hidden) resumeFeed(); });
  window.addEventListener('online', () => { if (!document.hidden) resumeFeed(); });
}

function startWatchdog() {
  if (staleTimer) return;
  staleTimer = setInterval(() => {
    if (!totalSubs() || suspended) return;
    if (lastMsgAt && Date.now() - lastMsgAt > STALE_MS) {
      // Socket claims OPEN but nothing is arriving → kill it so onclose fires
      // and the backoff reconnect runs. Reset the clock so we don't loop-kill.
      lastMsgAt = Date.now();
      if (ws) { try { ws.onclose = null; ws.close(); } catch {} ws = null; }
      statusListeners.forEach(f => { try { f('closed'); } catch {} });
      if (reconnectTimer) clearTimeout(reconnectTimer);
      backoffMs = 2000;
      reconnectTimer = setTimeout(ensureSocket, 300);
    }
  }, 4000);
}

function feedUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.hostname === 'localhost' ? 'localhost' : 'api.jaiswalcapital.com';
  const devPort = (import.meta as any).env?.VITE_BACKEND_PORT ?? 5001;
  const port = window.location.hostname === 'localhost' ? `:${devPort}` : '';
  return `${proto}//${host}${port}/ws/live-feed`;
}

function ensureSocket() {
  if (suspended) return;                                // page hidden/hidden-away on a phone: resumeFeed reopens
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  ws = new WebSocket(feedUrl());
  ws.onopen = () => {
    backoffMs = 2000;                                   // healthy connect resets the ladder
    lastMsgAt = Date.now();
    statusListeners.forEach(f => { try { f('open'); } catch {} });
  };
  ws.onmessage = ev => {
    lastMsgAt = Date.now();                             // feed the stale-watchdog
    let data: any;
    try { data = JSON.parse(ev.data); } catch { return; }
    listeners.forEach(f => { try { f(data); } catch {} });
    dispatchLtp(data);
  };
  ws.onclose = () => {
    statusListeners.forEach(f => { try { f('closed'); } catch {} });
    ws = null;
    if (totalSubs() && !suspended) {                    // someone still wants the feed
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(ensureSocket, backoffMs);
      backoffMs = Math.min(MAX_BACKOFF_MS, Math.round(backoffMs * 1.7));
    }
  };
  ws.onerror = () => { try { ws?.close(); } catch {} };
}

function teardownIfIdle() {
  if (totalSubs()) return;
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  if (staleTimer) { clearInterval(staleTimer); staleTimer = null; }
  if (ws) { ws.onclose = null; try { ws.close(); } catch {} ws = null; }
}

/** Subscribe to the raw shared live feed. Returns an unsubscribe function. */
export function subscribeFeed(onMsg: FeedListener, onStatus?: StatusListener): () => void {
  listeners.add(onMsg);
  if (onStatus) {
    statusListeners.add(onStatus);
    if (ws?.readyState === WebSocket.OPEN) { try { onStatus('open'); } catch {} }
  }
  ensureSocket();
  startWatchdog();
  return () => {
    listeners.delete(onMsg);
    if (onStatus) statusListeners.delete(onStatus);
    teardownIfIdle();
  };
}

/** Subscribe to one symbol's LTP over the shared socket. Fires immediately
 *  from cache when a recent tick exists. Returns an unsubscribe function. */
export function subscribeLTP(symbol: string, cb: LtpListener): () => void {
  const key = normSym(symbol);
  if (!key) return () => {};
  let set = ltpListeners.get(key);
  if (!set) { set = new Set(); ltpListeners.set(key, set); }
  set.add(cb);
  const cached = ltpCache.get(key);
  if (cached && Date.now() - cached.at < 15000) { try { cb(cached.ltp, cached.at); } catch {} }
  ensureSocket();
  startWatchdog();
  return () => {
    const s = ltpListeners.get(key);
    if (s) { s.delete(cb); if (!s.size) ltpListeners.delete(key); }
    teardownIfIdle();
  };
}

/** Sync peek at the last tick seen for a symbol (any subscriber's traffic warms this). */
export function getLastLTP(symbol: string): { ltp: number; at: number } | null {
  return ltpCache.get(normSym(symbol)) ?? null;
}
